import React, { useEffect, useRef, useState } from "react";
import "./CameraFrame.css";

/**
 * Wraps a react-webcam <Webcam> to:
 *  1. Widen the enclosing react-bootstrap modal and center its body (adds classes to the
 *     ancestor .modal-dialog / .modal-body on mount — no per-modal edits needed).
 *  2. Reserve a fixed 16:9 area and show a "Starting camera…" spinner until the stream is
 *     ready, so there is no blank whitespace / layout jump while the camera initialises.
 *  3. Overlay white corner brackets + a "PLACE ID WITHIN FRAME" hint so staff place the
 *     whole ID card inside the frame — filling the frame gives the PDF417 barcode enough
 *     pixels to decode.
 *
 * Usage: <CameraFrame><Webcam ... /></CameraFrame>
 */
export default function CameraFrame({ children }) {
  const rootRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const dialog = el.closest(".modal-dialog");
    if (dialog) dialog.classList.add("sf-id-capture-dialog");
    const body = el.closest(".modal-body");
    if (body) body.classList.add("sf-id-capture-body");
  }, []);

  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
        style: {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          ...(children.props.style || {}),
        },
        onUserMedia: (...args) => {
          setReady(true);
          if (typeof children.props.onUserMedia === "function") {
            children.props.onUserMedia(...args);
          }
        },
      })
    : children;

  const corner = {
    position: "absolute",
    width: 30,
    height: 30,
    borderColor: "#ffffff",
    borderStyle: "solid",
    borderWidth: 0,
  };

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#0a0c0f",
        borderRadius: 6,
        overflow: "hidden",
        lineHeight: 0,
      }}
    >
      {child}

      {/* camera-loading placeholder (no blank whitespace while the stream starts) */}
      {!ready && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            color: "#cbd5e1",
            fontSize: 13,
            lineHeight: 1.3,
          }}
        >
          <span className="sf-cam-spinner" />
          Starting camera…
        </div>
      )}

      {/* corner brackets */}
      <div
        style={{
          position: "absolute",
          top: "8%",
          left: "8%",
          right: "8%",
          bottom: "8%",
          pointerEvents: "none",
          opacity: ready ? 1 : 0,
        }}
      >
        <span style={{ ...corner, top: -2, left: -2, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 6 }} />
        <span style={{ ...corner, top: -2, right: -2, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 6 }} />
        <span style={{ ...corner, bottom: -2, left: -2, borderBottomWidth: 3, borderLeftWidth: 3, borderBottomLeftRadius: 6 }} />
        <span style={{ ...corner, bottom: -2, right: -2, borderBottomWidth: 3, borderRightWidth: 3, borderBottomRightRadius: 6 }} />
      </div>

      {/* instruction label (shown for front and back once the camera is live) */}
      {ready && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "6%",
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              background: "rgba(0,0,0,0.6)",
              color: "#ffffff",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.09em",
              lineHeight: 1.2,
              padding: "6px 14px",
              borderRadius: 999,
            }}
          >
            PLACE ID WITHIN FRAME
          </span>
        </div>
      )}
    </div>
  );
}
